// Copyright (c) 2026, Dokos SAS and Contributors
// License: GNU General Public License v3. See license.txt
// @dokos: Plan mode of the Project Management view. Projects are grouped by Project Type;
// one resource-timeline row per project shows, on a Tasks | Team switch, either its task
// tree (bars over their expected dates, subtasks under their parent, then a quick "add a
// task" row) or its employees (daily work chips). A task with subtasks or assignees has the
// same Subtasks | Team switch; a "Team load" row sums load / capacity per day and
// expands into one row per employee (load across all projects), as the former Assign mode.
// Projects and tasks have an "assign" button; a chip dropped on another employee row of
// its project reassigns its Task Assignment.
// Resources and events come from ONE `get_plan` request per date range and filters.

import interactionPlugin from "@fullcalendar/interaction";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";

import { PMViewBaseCalendar } from "./PMViewBaseCalendar";
import { openProjectQuickEntry } from "./PMProjectQuickEntry";
import { openProjectDialog, openTaskDialog } from "./PMRecordDialog";
import {
	activityColor,
	avatar,
	avatarStack,
	employeePicker,
	formatDay,
	formatDayRange,
	formatHours,
	formatMinutes,
	formatMinutesShort,
	groupProjectsByType,
	h,
	icon,
	loadLevel,
	overMark,
	projectStatusPill,
	remainingText,
	renderChip,
	safeBoldHtml,
	swatch,
	taskStatusDot,
	taskStatusLabel,
	taskTree,
	ymd,
} from "./PMRender";
import { closePopover, openPopover, popoverFact, popoverItem } from "./PMPopover";
import { openTaskAssignmentDialog } from "./PMTaskAssignmentDialog";

const PLAN_METHOD = "erpnext.projects.project_management.get_plan";
const QUICK_TASK_METHOD = "erpnext.projects.project_management.create_task_quick";
// @dokos: contextual actions on the assignments (chip, coverage and assignee row menus)
const ACTIONS = {
	removeDays: "erpnext.projects.project_management.remove_assignment_days",
	delete: "erpnext.projects.project_management.delete_task_assignment",
	restore: "erpnext.projects.project_management.restore_task_assignment",
	removeFromTask: "erpnext.projects.project_management.remove_employee_from_task",
	replaceOnTask: "erpnext.projects.project_management.replace_employee_on_task",
	datesFromAssignments: "erpnext.projects.project_management.set_task_dates_from_assignments",
};
const UNDO_SECONDS = 8;
const LOAD_RESOURCE_ID = "__load__";
const TEAM_ADD_RESOURCE_ID = "__team_add__"; // @dokos
export const TEAM_EXPANDED_KEY = "__team__"; // @dokos: Team section state in `expandedProjects`
const SELECTABLE_KINDS = ["project", "employee", "task", "taskemployee", "team"];
const USER_SETTINGS_KEY = "pm_view"; // @dokos: `extra_employees` of the Team section
const DEFAULT_ASSIGNMENT_DAYS = 5; // @dokos: default length of an assignment from a project row

/** @dokos: expanded state of the group, project and task rows, kept for the whole desk session. */
const expandedProjects = new Map();
/** @dokos: Tasks | Team choice of the project rows, Subtasks | Team of the task rows. */
const rowModes = new Map();

const resourceId = {
	group: (type) => `group::${type || ""}`,
	project: (project) => `project::${project}`,
	employee: (project, employee) => `emp::${project}::${employee}`,
	task: (task) => `task::${task}`,
	taskEmployee: (task, employee) => `taskemp::${task}::${employee}`,
	add: (project) => `add::${project}`,
	team: (employee) => `team::${employee}`,
};

const kindOf = (resource) => resource?.extendedProps?.kind;
/** @dokos: sum of the minutes of a `{day: minutes}` map. */
const sumValues = (perDay) => Object.values(perDay || {}).reduce((sum, minutes) => sum + (minutes || 0), 0);
const addDays = (date, days) => moment(date, "YYYY-MM-DD").add(days, "days").format("YYYY-MM-DD");
const datePart = (value) => (value ? String(value).slice(0, 10) : null);
/** Whole days between two local midnights (rounded: a DST day lasts 23 or 25 hours). */
const dayDelta = (from, to) => Math.round(moment(to).diff(moment(from), "hours") / 24);

/**
 * @dokos: "Over 12 Nov" pill (red = needs action): the first day over capacity, "+N" for the
 * others, every day in the tooltip. "Over" alone read as "over the task budget".
 * @param {string[]} days ISO days, sorted
 */
const overPill = (days) =>
	erpnext.dokos.ui.pill({
		label:
			days.length > 1
				? __("Over {0} +{1}", [formatDay(days[0], "dayMonth"), days.length - 1], "PM View")
				: __("Over {0}", [formatDay(days[0], "dayMonth")], "PM View"),
		theme: "red",
		title: __(
			"Over capacity on {0} (all projects)",
			[days.map((day) => formatDay(day, "weekdayDayMonth")).join(", ")],
			"PM View"
		),
	})[0];

/** @dokos */
export class PMViewCalendar_Plan extends PMViewBaseCalendar {
	eventSources = [];

	/**
	 * @param {Object} opts
	 * @param {unknown} opts.page
	 * @param {import("./ProjectManagementView.js").ProjectManagementView} opts.view
	 * @param {HTMLElement} opts.parent
	 */
	constructor(opts) {
		super(opts);
		this.page = opts.page;
		this.pickers = []; // @dokos: employee pickers (their popovers outlive the cells)
		this._cache = { key: null, promise: null };
		this.payload = null;
		this.legendContainer = null;
	}

	// -------------------------------------------------------------------------------
	// Lifecycle
	// -------------------------------------------------------------------------------

	async make() {
		this.page.clear_user_actions();
		await super.make();
		this.calendar_element.classList.add("fc--pm-plan");

		this.footnote_area = frappe.utils.set_footnote(
			this.footnote_area,
			this.parent,
			__(
				"Select days on a row, or use the assign buttons, to create a Task Assignment. Drag a chip to move its whole assignment, or onto another employee of the project to reassign it; drag or stretch a task bar to change the task dates.",
				null,
				"PM View"
			)
		);
		this.footnote_area.css({ "border-top": "0px" });
	}

	destroy() {
		this.disposePickers();
		closePopover({ restoreFocus: false });
		super.destroy();
		this.footnote_area?.remove();
	}

	disposePickers() {
		for (const picker of this.pickers) $(picker).popover("dispose");
		this.pickers = [];
	}

	refresh() {
		closePopover({ restoreFocus: false }); // @dokos: its element is about to be redrawn
		this._cache = { key: null, promise: null };
		super.refresh();
	}

	setupLegend(container) {
		this.legendContainer = container;
		this.renderLegend();
	}

	/** @override @dokos: the timeline periods (same views as before, now an es-tab-buttons). */
	getPeriodViews() {
		return [
			["pmWeek", __("Week")],
			["pm2Weeks", __("2 weeks", null, "PM View")],
			["pmMonth", __("Month")],
		];
	}

	// -------------------------------------------------------------------------------
	// Data
	// -------------------------------------------------------------------------------

	getPlanFilters() {
		return this.view?.doctype === "Project" ? this.getFilters() : [];
	}

	/** One request per [start, end, filters]; resources and events share it. */
	getPlan({ start, end }) {
		const args = {
			start: ymd(start),
			end: ymd(moment(end).subtract(1, "day")), // exclusive in FullCalendar
			filters: this.getPlanFilters(),
			employees: this.getExtraEmployees(), // @dokos: Team section members added by hand
		};
		const key = JSON.stringify(args);
		if (this._cache.key !== key || !this._cache.promise) {
			const promise = frappe.xcall(PLAN_METHOD, args).then((payload) => {
				this.indexPayload(payload);
				if (this._cache.promise === promise) {
					this.payload = payload;
					this.renderLegend();
				}
				return payload;
			});
			promise.catch(() => {
				if (this._cache.promise === promise) this._cache = { key: null, promise: null };
			});
			this._cache = { key, promise };
		}
		return this._cache.promise;
	}

	/** Lookup tables built once per payload. */
	indexPayload(payload) {
		if (payload._index) return payload;
		const employees = {};
		const chipsByProjectEmployee = {};
		const minutesByProjectDay = {};
		const typeByTask = {};
		// @dokos: task tree per project; a task's team and chips include its subtasks'
		const trees = {};
		const taskParent = {};
		const taskTeam = {};
		const chipsByTaskEmployee = {};
		const chipsByTask = {}; // @dokos: the work of a task and its subtasks (coverage)
		const taskByName = {};
		for (const project of payload.projects) {
			for (const emp of project.employees) employees[emp.employee] = emp;
			for (const task of project.tasks) taskByName[task.name] = task;
			const tree = (trees[project.name] = taskTree(project.tasks));
			Object.assign(taskParent, tree.parent);
			const teamOf = (task) => {
				const team = new Set(task.assigned_employees);
				for (const child of tree.children[task.name] || [])
					for (const e of teamOf(child)) team.add(e);
				taskTeam[task.name] = [...team];
				return team;
			};
			tree.roots.forEach(teamOf);
		}
		for (const ev of payload.events) {
			const key = `${ev.project}::${ev.employee}`;
			chipsByProjectEmployee[key] = chipsByProjectEmployee[key] || [];
			chipsByProjectEmployee[key].push(ev);
			for (let task = ev.task; task; task = taskParent[task]) {
				const taskKey = `${task}::${ev.employee}`;
				(chipsByTaskEmployee[taskKey] = chipsByTaskEmployee[taskKey] || []).push(ev);
				(chipsByTask[task] = chipsByTask[task] || []).push(ev);
			}
			const day = (minutesByProjectDay[ev.project] = minutesByProjectDay[ev.project] || {});
			day[ev.date] = (day[ev.date] || 0) + (ev.minutes || 0);
			if (ev.task && ev.activity_type && !typeByTask[ev.task]) {
				typeByTask[ev.task] = { name: ev.activity_type, color: ev.activity_color };
			}
		}
		payload._index = {
			employees,
			chipsByProjectEmployee,
			chipsByTaskEmployee,
			chipsByTask,
			taskByName,
			minutesByProjectDay,
			typeByTask,
			trees,
			taskParent,
			taskTeam,
		};
		return payload;
	}

	/** @dokos: employees added with "+ Employee", kept in the user settings of the list. */
	getExtraEmployees() {
		const settings = frappe.get_user_settings(this.view?.doctype, USER_SETTINGS_KEY) || {};
		return Array.isArray(settings.extra_employees) ? settings.extra_employees : [];
	}

	/** @dokos */
	async setExtraEmployees(employees) {
		const doctype = this.view?.doctype;
		if (!doctype) return;
		await frappe.model.user_settings.save(doctype, USER_SETTINGS_KEY, {
			extra_employees: [...new Set(employees)],
		});
		this.refresh();
	}

	isOver(employee, day) {
		const load = this.payload?.load?.[employee]?.[day] || 0;
		const capacity = this.payload?.capacity?.[employee]?.[day] || 0;
		return load > capacity;
	}

	/**
	 * @dokos: the days `employee` is over capacity (all projects), sorted; only among `days`
	 * when given (the days of a row's own work).
	 */
	overDays(employee, days = null) {
		const candidates = days ? [...new Set(days)] : Object.keys(this.payload?.load?.[employee] || {});
		return candidates.filter((day) => this.isOver(employee, day)).sort();
	}

	isExpanded(project, index) {
		return expandedProjects.has(project) ? expandedProjects.get(project) : index === 0;
	}

	async fetchResources(info) {
		const payload = await this.getPlan(info);
		return this.buildResources(payload);
	}

	async fetchEvents(info) {
		const payload = await this.getPlan(info);
		return this.buildEvents(payload);
	}

	/** @dokos: a saved expanded state, else `fallback`. */
	isOpen(key, fallback) {
		return expandedProjects.has(key) ? expandedProjects.get(key) : fallback;
	}

	/** @dokos: the saved switch choice of a row if it is still `allowed`, else the first allowed. */
	modeOf(key, allowed) {
		const mode = rowModes.get(key);
		return allowed.includes(mode) ? mode : allowed[0];
	}

	setMode(key, mode, rowId) {
		rowModes.set(key, mode);
		this.fullcalendar?.refetchResources();
		// the row is re-rendered: give the focus back to its (new) switch
		setTimeout(() => {
			const id = CSS.escape(rowId);
			this.calendar_element
				?.querySelector(`td[data-resource-id="${id}"] .es-tab-buttons [aria-checked="true"]`)
				?.focus();
		}, 50);
	}

	/**
	 * @dokos: the project rows in display order, as `{id, title, props}`: shared by the
	 * timeline (resources) and the phone layout (cards).
	 */
	planRows(payload) {
		const rows = [];
		const groups = groupProjectsByType(payload.projects);
		const withGroups = groups.some((group) => group.type); // no header for "untyped" alone
		let index = 0;
		for (const group of groups) {
			const id = resourceId.group(group.type);
			const expanded = withGroups ? this.isOpen(id, true) : true;
			if (withGroups) {
				rows.push({ id, title: group.label, props: { kind: "group", group, expanded } });
			}
			for (const project of group.projects) {
				const projectIndex = index++;
				if (expanded) rows.push(...this.projectRows(payload, project, projectIndex));
			}
		}
		return rows;
	}

	projectRows(payload, project, index) {
		const { chipsByProjectEmployee, trees } = payload._index;
		const expanded = this.isExpanded(project.name, index);
		// @dokos: Tasks stays available without tasks, for the "add a task" row
		const modes = project.employees.length ? ["tasks", "team"] : ["tasks"];
		const mode = this.modeOf(project.name, modes);
		const rows = [
			{
				id: resourceId.project(project.name),
				title: project.project_name || project.name,
				props: { kind: "project", project, expanded, mode },
			},
		];
		if (!expanded) return rows;

		if (mode === "team") {
			for (const emp of project.employees) {
				const chips = chipsByProjectEmployee[`${project.name}::${emp.employee}`] || [];
				rows.push({
					id: resourceId.employee(project.name, emp.employee),
					title: emp.employee_name,
					props: this.employeeProps("employee", { project, employee: emp, chips }),
				});
			}
			return rows;
		}
		for (const task of trees[project.name].roots) this.pushTaskRows(payload, rows, project, task, 0);
		if (frappe.model.can_create("Task")) {
			rows.push({ id: resourceId.add(project.name), title: "", props: { kind: "add", project } });
		}
		return rows;
	}

	/** @dokos: a task row, then (expanded) its subtasks or its team, recursively. */
	pushTaskRows(payload, rows, project, task, depth) {
		const { employees, chipsByTaskEmployee, trees, taskTeam } = payload._index;
		const children = trees[project.name].children[task.name] || [];
		const team = (taskTeam[task.name] || []).map((e) => employees[e] || { employee: e });
		const key = resourceId.task(task.name);
		const modes = [children.length && "subtasks", team.length && "team"].filter(Boolean);
		// subtasks are shown under their parent by default, a task's team on demand
		const expanded = modes.length > 0 && this.isOpen(key, children.length > 0);
		const mode = this.modeOf(key, modes);
		rows.push({
			id: key,
			title: task.subject,
			props: { kind: "task", project, task, assignees: team, depth, children, expanded, mode },
		});
		if (!expanded) return;
		if (mode === "subtasks") {
			for (const child of children) this.pushTaskRows(payload, rows, project, child, depth + 1);
			return;
		}
		for (const emp of team) {
			const chips = chipsByTaskEmployee[`${task.name}::${emp.employee}`] || [];
			rows.push({
				id: resourceId.taskEmployee(task.name, emp.employee),
				title: emp.employee_name,
				props: this.employeeProps("taskemployee", {
					project,
					task,
					employee: emp,
					chips,
					depth: depth + 1,
				}),
			});
		}
	}

	employeeProps(kind, { chips, ...props }) {
		return {
			kind,
			...props,
			chips,
			minutes: chips.reduce((sum, ev) => sum + (ev.minutes || 0), 0),
			activityType: this.mainActivityType(chips),
		};
	}

	buildResources(payload) {
		const resources = [];
		let order = 0;
		const push = (resource) => resources.push({ ...resource, order: order++ });

		for (const { id, title, props } of this.planRows(payload)) {
			push({ id, title, extendedProps: props });
		}

		const teamExpanded = expandedProjects.get(TEAM_EXPANDED_KEY) || false;
		push({
			id: LOAD_RESOURCE_ID,
			title: __("Team load", null, "PM View"),
			extendedProps: { kind: "load", expanded: teamExpanded },
		});
		if (teamExpanded) {
			const extra = new Set(this.getExtraEmployees());
			for (const emp of payload.team || []) {
				push({
					id: resourceId.team(emp.employee),
					title: emp.employee_name,
					extendedProps: {
						kind: "team",
						employee: emp,
						extra: extra.has(emp.employee),
						minutes: sumValues(payload.load?.[emp.employee]),
						capacity: sumValues(payload.capacity?.[emp.employee]), // @dokos: remaining capacity
					},
				});
			}
			push({ id: TEAM_ADD_RESOURCE_ID, title: "", extendedProps: { kind: "teamadd" } });
		}
		return resources;
	}

	mainActivityType(chips) {
		const count = {};
		for (const chip of chips) {
			if (chip.activity_type) count[chip.activity_type] = (count[chip.activity_type] || 0) + 1;
		}
		return Object.entries(count).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
	}

	buildEvents(payload) {
		const { employees, minutesByProjectDay, typeByTask, taskParent, taskTeam, trees, chipsByTask } =
			payload._index;
		const canWriteTA = frappe.model.can_write("Task Assignment");
		const canWriteTask = frappe.model.can_write("Task");
		const events = [];

		// Daily work chips on the employee rows of the project (Team), and of its task and
		// every parent task (task Team rows); rows that are not displayed ignore theirs
		for (const ev of payload.events) {
			const rows = [{ id: ev.id, resourceId: resourceId.employee(ev.project, ev.employee) }];
			for (let task = ev.task; task; task = taskParent[task]) {
				rows.push({
					id: `${ev.id}::${task}`,
					resourceId: resourceId.taskEmployee(task, ev.employee),
					task,
				});
			}
			for (const row of rows) events.push(this.chipEvent(ev, row, employees, canWriteTA));
		}

		// Daily totals on the project rows
		for (const project of payload.projects) {
			const capacityOf = (day) =>
				project.employees.reduce((sum, e) => sum + (payload.capacity?.[e.employee]?.[day] || 0), 0);
			for (const [day, minutes] of Object.entries(minutesByProjectDay[project.name] || {})) {
				events.push({
					id: `sum::${project.name}::${day}`,
					resourceId: resourceId.project(project.name),
					start: day,
					end: addDays(day, 1),
					allDay: true,
					display: "background",
					classNames: ["pm-sum"],
					extendedProps: { kind: "sum", minutes, capacity: capacityOf(day) },
				});
			}

			for (const task of project.tasks) {
				// @dokos: the task row shows its work (and its subtasks') day by day, whether the
				// task has expected dates or not
				const segments = this.coverageSegments(chipsByTask[task.name] || []);
				for (const segment of segments) {
					events.push(this.coverageEvent(project, task, segment, employees));
				}
				// @dokos: with work shown on the row, no bar of the expected dates: over several
				// weeks it reads as a scrollbar
				if (segments.length) continue;

				// Task bars over the expected dates
				const start = datePart(task.exp_start_date) || datePart(task.exp_end_date);
				if (!start) continue;
				const end = datePart(task.exp_end_date) || start;
				const isParent = Boolean(trees[project.name].children[task.name]);
				// @dokos: a parent bar names the people of its subtasks
				const assignees = (isParent ? taskTeam[task.name] : task.assigned_employees).map(
					(e) => employees[e] || { employee: e }
				);
				const type = typeByTask[task.name];
				events.push({
					id: resourceId.task(task.name),
					resourceId: resourceId.task(task.name),
					start,
					end: addDays(end < start ? start : end, 1),
					allDay: true,
					editable: canWriteTask,
					resourceEditable: false,
					classNames: [
						"pm-task-bar",
						// @dokos: a parent task spans its subtasks, it is drawn lighter than them
						// and its own assignees are not the point (its subtasks carry the work)
						isParent ? "pm-task-bar--parent" : assignees.length ? "" : "pm-task-bar--unassigned",
					].filter(Boolean),
					extendedProps: {
						kind: "task",
						task,
						assignees,
						activityType: type?.name,
						color: activityColor(type?.name, type?.color, project.name),
					},
				});
			}
		}

		// Team load row
		for (const [day, totals] of Object.entries(this.teamLoad(payload))) {
			events.push({
				id: `load::${day}`,
				resourceId: LOAD_RESOURCE_ID,
				start: day,
				end: addDays(day, 1),
				allDay: true,
				display: "background",
				classNames: ["pm-load"],
				extendedProps: { kind: "load", ...totals },
			});
		}

		// @dokos: Team section, load / capacity per employee and day (all projects)
		if (expandedProjects.get(TEAM_EXPANDED_KEY)) {
			const days = this.visibleDays(payload);
			for (const emp of payload.team || []) {
				for (const day of days) {
					const load = payload.load?.[emp.employee]?.[day] || 0;
					const capacity = payload.capacity?.[emp.employee]?.[day] || 0;
					if (!load && !capacity) continue;
					events.push({
						id: `team::${emp.employee}::${day}`,
						resourceId: resourceId.team(emp.employee),
						start: day,
						end: addDays(day, 1),
						allDay: true,
						display: "background",
						classNames: ["pm-load"],
						extendedProps: { kind: "load", load, capacity },
					});
				}
			}
		}
		return events;
	}

	/** A daily work chip of `ev` on the row `row.resourceId` (`row.task`: a task Team row). */
	chipEvent(ev, row, employees, canWriteTA) {
		const employee = employees[ev.employee] || { employee: ev.employee };
		const over = this.isOver(ev.employee, ev.date);
		return {
			id: row.id,
			resourceId: row.resourceId,
			start: ev.date,
			end: addDays(ev.date, 1),
			allDay: true,
			editable: canWriteTA,
			durationEditable: false,
			resourceEditable: canWriteTA, // @dokos: drop on another employee row = reassign
			classNames: ["pm-chip", over ? "pm-over" : ""].filter(Boolean),
			extendedProps: {
				kind: "chip",
				data: ev,
				rowTask: row.task || null, // @dokos: chip of a task Team row
				over,
				color: activityColor(ev.activity_type, ev.activity_color, ev.project), // @dokos: hash fallback
				tooltip: [
					employee.employee_name || ev.employee,
					formatDay(ev.date, "weekdayDayMonth"),
					[formatMinutes(ev.minutes), ev.activity_type && __(ev.activity_type)]
						.filter(Boolean)
						.join(" "),
					over ? __("over capacity", null, "PM View") : null,
				]
					.filter(Boolean)
					.join(" · "),
			},
		};
	}

	/**
	 * @dokos: the work chips of a task row grouped into runs of consecutive days with the
	 * same people: `{start, end, employees, days: [{date, minutes, over, chips}]}`.
	 */
	coverageSegments(chips) {
		const byDay = new Map();
		for (const ev of chips) {
			const day = byDay.get(ev.date) || { date: ev.date, minutes: 0, employees: new Set(), chips: [] };
			day.minutes += ev.minutes || 0;
			day.employees.add(ev.employee);
			day.chips.push(ev);
			byDay.set(ev.date, day);
		}
		const segments = [];
		for (const date of [...byDay.keys()].sort()) {
			const day = byDay.get(date);
			const employees = [...day.employees].sort();
			day.over = employees.some((employee) => this.isOver(employee, date));
			const last = segments[segments.length - 1];
			if (last && addDays(last.end, 1) === date && last.employees.join() === employees.join()) {
				last.end = date;
				last.days.push(day);
			} else {
				segments.push({ start: date, end: date, employees, days: [day] });
			}
		}
		return segments;
	}

	/** @dokos: a coverage segment of a task row (background: the task bars and selection stay usable). */
	coverageEvent(project, task, segment, employees) {
		const people = segment.employees.map((e) => employees[e] || { employee: e });
		return {
			id: `cov::${task.name}::${segment.start}`,
			resourceId: resourceId.task(task.name),
			start: segment.start,
			end: addDays(segment.end, 1),
			allDay: true,
			display: "background",
			classNames: ["pm-cov", segment.days.some((d) => d.over) && "pm-cov--over"].filter(Boolean),
			extendedProps: { kind: "coverage", project, task, segment, people },
		};
	}

	/** `{day: {load, capacity}}` summed over every employee of the plan. */
	teamLoad(payload) {
		const out = {};
		for (const day of this.visibleDays(payload)) out[day] = { load: 0, capacity: 0 };
		for (const perDay of Object.values(payload.capacity || {})) {
			for (const [day, minutes] of Object.entries(perDay)) {
				if (out[day]) out[day].capacity += minutes || 0;
			}
		}
		for (const perDay of Object.values(payload.load || {})) {
			for (const [day, minutes] of Object.entries(perDay)) {
				if (out[day]) out[day].load += minutes || 0;
			}
		}
		return out;
	}

	visibleDays(payload) {
		const days = [];
		for (let day = payload.start; day <= payload.end && days.length < 400; day = addDays(day, 1)) {
			days.push(day);
		}
		return days;
	}

	// -------------------------------------------------------------------------------
	// FullCalendar options
	// -------------------------------------------------------------------------------

	/** @override */ get_calendar_options() {
		return {
			...super.get_calendar_options(),

			schedulerLicenseKey: frappe.boot.fullcalendar_scheduler_licence_key,
			plugins: [resourceTimelinePlugin, interactionPlugin],

			height: "auto",
			stickyHeaderDates: true,
			resourceAreaWidth: "460px", // @dokos: room for the Tasks | Team switch

			initialView: "pm2Weeks",
			views: {
				pmBase: {
					type: "resourceTimeline",
					slotDuration: { days: 1 },
					slotLabelFormat: [{ weekday: "short", day: "numeric" }],
					nowIndicator: false,
				},
				pmWeek: {
					type: "pmBase",
					duration: { weeks: 1 },
					slotMinWidth: 90,
					buttonText: __("Week"),
				},
				pm2Weeks: {
					type: "pmBase",
					duration: { weeks: 2 },
					slotMinWidth: 70,
					buttonText: __("2 weeks", null, "PM View"),
				},
				pmMonth: {
					type: "pmBase",
					duration: { months: 1 },
					slotMinWidth: 44,
					buttonText: __("Month"),
				},
			},
			headerToolbar: {
				left: "prev,next today",
				center: "title",
				right: "", // @dokos: period switch in the view header (setupPeriodSwitch)
			},

			editable: true,
			eventResourceEditable: false,
			eventInteractive: true,
			selectable: frappe.model.can_create("Task Assignment"),
			selectMirror: false,
			refetchResourcesOnNavigate: true,
			weekends: true,

			resources: this.fetchResources.bind(this),
			events: this.fetchEvents.bind(this),
			resourceOrder: "order",
			resourceAreaColumns: [
				{
					headerContent: __("Project · Employee · Task", null, "PM View"),
					field: "title",
					cellContent: (arg) => ({ domNodes: [this.renderResourceCell(arg.resource)] }),
					cellClassNames: (arg) => ["pm-res", `pm-res--${kindOf(arg.resource)}`],
				},
				{
					headerContent: __("Planned", null, "PM View"),
					field: "order",
					width: 90,
					cellContent: (arg) => ({ domNodes: [this.renderPlannedCell(arg.resource)] }),
					cellClassNames: (arg) => ["pm-res-planned", `pm-res--${kindOf(arg.resource)}`],
				},
			],
			resourceLaneClassNames: (arg) => [`pm-lane--${kindOf(arg.resource)}`],

			eventOrder: "start,title",
			selectAllow: (info) => SELECTABLE_KINDS.includes(kindOf(info.resource)),
			eventAllow: (info, event) => this.eventAllow(info, event), // @dokos
		};
	}

	// -------------------------------------------------------------------------------
	// Resource cells
	// -------------------------------------------------------------------------------

	renderResourceCell(resource) {
		const props = resource.extendedProps;
		switch (props.kind) {
			case "group":
				return this.renderGroupCell(props);
			case "project":
				return this.renderProjectCell(props);
			case "employee":
			case "taskemployee":
				return this.renderEmployeeCell(props);
			case "task":
				return this.renderTaskCell(props);
			case "add":
				return this.renderAddCell(props);
			case "load":
				return this.renderLoadCell(props);
			case "team":
				return this.renderTeamCell(props);
			case "teamadd":
				return this.renderTeamAddCell();
			default:
				return h("span", { text: resource.title });
		}
	}

	/** @dokos: chevron of a group, project or task row. */
	toggleButton({ expanded, name, key, rowId }) {
		return h(
			"button",
			{
				type: "button",
				class: ["pm-toggle", expanded && "is-expanded"],
				attrs: {
					"aria-expanded": expanded ? "true" : "false",
					"aria-label": expanded
						? __("Collapse {0}", [name], "PM View")
						: __("Expand {0}", [name], "PM View"),
				},
				on: { click: () => this.toggleProject(key, !expanded, rowId) },
			},
			icon("chevron-right", "sm")
		);
	}

	/**
	 * @dokos: Tasks | Team (project) or Subtasks | Team (task) switch of an expanded row.
	 * @param {Array<[string, string, number]>} choices `[mode, label, count]`
	 */
	modeSwitch({ key, rowId, mode, choices, name }) {
		const count = (n) => h("span", { class: "pm-mode-count", text: String(n) });
		const el = erpnext.dokos.ui.tab_buttons({
			label: __("Show in {0}", [name], "PM View"),
			value: mode,
			css_class: "pm-row-switch",
			options: choices.map(([value, label, n]) => ({
				value,
				label,
				suffix: () => count(n),
			})),
			on_change: (value) => this.setMode(key, value, rowId),
		})[0];
		el.setAttribute("data-pm-key", `mode::${key}`); // @dokos: focus kept on the phone layout
		return el;
	}

	/** @dokos: a Project Type separator; the projects without a type come last. */
	renderGroupCell({ group, expanded }) {
		const id = resourceId.group(group.type);
		const toggle = this.toggleButton({ expanded, name: group.label, key: id, rowId: id });
		toggle.setAttribute("data-pm-key", `toggle::${id}`);
		return h(
			"div",
			{ class: "pm-cell pm-cell--group" },
			toggle,
			h("span", { class: "pm-group-title ellipsis", text: group.label }),
			h("span", { class: "pm-group-count", text: String(group.projects.length) })
		);
	}

	renderProjectCell({ project, expanded, mode }) {
		const name = project.project_name || project.name;
		const toggle = this.toggleButton({
			expanded,
			name,
			key: project.name,
			rowId: resourceId.project(project.name),
		});
		// @dokos: nothing to switch to without a team
		const withSwitch = expanded && project.employees.length > 0;
		const subtitle = [project.name !== project.project_name ? project.name : null, project.customer]
			.filter(Boolean)
			.join(" · ");
		return h(
			"div",
			{ class: "pm-cell pm-cell--project" },
			toggle,
			h(
				"div",
				{ class: "pm-cell-main" },
				h(
					"div",
					{ class: "pm-cell-line" },
					this.dialogLink({
						class: "pm-cell-title ellipsis",
						doctype: "Project",
						name: project.name,
						text: project.project_name || project.name,
						open: () => this.openProject(project),
					}),
					projectStatusPill(project.status)
				),
				// @dokos: the switch sits under the title, which keeps the whole width
				subtitle || withSwitch
					? h(
							"div",
							{ class: "pm-cell-line pm-cell-line--sub" },
							subtitle ? h("span", { class: "pm-cell-sub ellipsis", text: subtitle }) : null,
							withSwitch
								? this.modeSwitch({
										key: project.name,
										rowId: resourceId.project(project.name),
										mode,
										name,
										choices: [
											["tasks", __("Tasks", null, "PM View"), project.tasks.length],
											["team", __("Team", null, "PM View"), project.employees.length],
										],
								  })
								: null
					  )
					: null
			),
			avatarStack(project.employees),
			this.assignButton(
				__("Assign someone to {0}", [project.project_name || project.name], "PM View"),
				() => this.openAssignment({ project: project.name, ...this.defaultAssignmentDates(project) }),
				{ quiet: project.employees.length > 0 }
			)
		);
	}

	/**
	 * @dokos: "+ Assign" button of the project and task rows. A dashed circle did not read as
	 * a button; the words do. `quiet`: shown on row hover or focus only (the row already has
	 * people), so busy plans stay calm; a row without anyone keeps it visible.
	 */
	assignButton(label, action, { quiet = false } = {}) {
		if (!frappe.model.can_create("Task Assignment")) return null;
		const button = erpnext.dokos.ui.button({
			label: __("Assign", null, "PM View"),
			icon: "plus",
			size: "sm",
			variant: "ghost",
			title: label,
			css_class: ["pm-assign-btn", quiet && "pm-assign-btn--quiet"].filter(Boolean).join(" "),
			attrs: { "aria-label": label },
		})[0];
		button.addEventListener("click", action);
		return button;
	}

	/** @dokos: "⋯" button opening the actions of a row. */
	menuButton(label, open) {
		const button = erpnext.dokos.ui.button({
			icon: "ellipsis",
			size: "sm",
			variant: "ghost",
			title: label,
			css_class: "pm-row-menu-btn",
			attrs: { "aria-haspopup": "dialog" },
		})[0];
		button.addEventListener("click", () => open(button));
		return button;
	}

	/** @dokos: from today (or the first visible day) to the project end, within the view. */
	defaultAssignmentDates(project) {
		const view = this.fullcalendar?.view;
		const first = view ? ymd(view.activeStart) : frappe.datetime.get_today();
		const last = view ? addDays(ymd(view.activeEnd), -1) : addDays(first, 30);
		const today = frappe.datetime.get_today();
		const start = today > first && today <= last ? today : first;
		const projectEnd = datePart(project.expected_end_date);
		let end =
			projectEnd && projectEnd >= start ? projectEnd : addDays(start, DEFAULT_ASSIGNMENT_DAYS - 1);
		if (end > last) end = last;
		return { start_date: start, end_date: end };
	}

	/** @dokos: the task expected dates, else the project defaults (undated task). */
	taskAssignmentDates(project, task) {
		const start = datePart(task.exp_start_date) || datePart(task.exp_end_date);
		if (!start) return this.defaultAssignmentDates(project);
		const end = datePart(task.exp_end_date) || start;
		return { start_date: start, end_date: end < start ? start : end };
	}

	/** @dokos: the Team load row, with the toggle of the Team section. */
	renderLoadCell({ expanded }) {
		const toggle = h(
			"button",
			{
				type: "button",
				class: ["pm-toggle", expanded && "is-expanded"],
				attrs: {
					"aria-expanded": expanded ? "true" : "false",
					"aria-label": expanded
						? __("Collapse the team", null, "PM View")
						: __("Expand the team", null, "PM View"),
				},
				on: { click: () => this.toggleProject(TEAM_EXPANDED_KEY, !expanded, LOAD_RESOURCE_ID) },
			},
			icon("chevron-right", "sm")
		);
		return h(
			"div",
			{ class: "pm-cell pm-cell--load" },
			toggle,
			h(
				"div",
				{ class: "pm-cell-main" },
				h("span", { class: "pm-cell-title", text: __("Team load", null, "PM View") }),
				h("span", {
					class: "pm-cell-sub",
					text: __("Remaining · assigned (holidays and leave applied)", null, "PM View"),
				})
			)
		);
	}

	/** @dokos: one employee of the Team section (load across all projects). */
	renderTeamCell({ employee, extra }) {
		const over = this.overDays(employee.employee);
		const name = employee.employee_name || employee.employee;
		return h(
			"div",
			{ class: "pm-cell pm-cell--employee pm-cell--team" },
			avatar(employee, "sm"),
			h(
				"div",
				{ class: "pm-cell-main" },
				h("span", { class: "pm-cell-title ellipsis", text: name }),
				h("span", {
					class: "pm-cell-sub ellipsis",
					text: extra ? __("Added by you", null, "PM View") : __("All projects", null, "PM View"),
				})
			),
			over.length
				? overPill(over) // @dokos: es-badge through the status map
				: null,
			extra
				? h(
						"button",
						{
							type: "button",
							class: "pm-toggle pm-remove-btn",
							title: __("Remove from my team rows", null, "PM View"),
							attrs: { "aria-label": __("Remove {0} from my team rows", [name], "PM View") },
							on: {
								click: () =>
									this.setExtraEmployees(
										this.getExtraEmployees().filter((e) => e !== employee.employee)
									),
							},
						},
						icon("x", "xs")
				  )
				: null
		);
	}

	/** @dokos: "+ Employee" row of the Team section. */
	renderTeamAddCell() {
		this.disposePickers(); // the previous render of this row is gone
		const picker = employeePicker({
			label: __("Employee"),
			container: this.page.parent?.[0] || document.body,
			exclude: () => (this.payload?.team || []).map((e) => e.employee),
			onPick: (employee) => this.setExtraEmployees([...this.getExtraEmployees(), employee]),
		});
		this.pickers.push(picker);
		return h("div", { class: "pm-cell pm-cell--employee pm-cell--team-add" }, picker);
	}

	renderEmployeeCell({ kind, employee, activityType, depth, chips = [], project, task, minutes }) {
		// @dokos: over capacity on a day of THIS row's work only, not anywhere in the period
		const over = this.overDays(
			employee.employee,
			chips.map((ev) => ev.date)
		);
		const menu =
			kind === "taskemployee" && frappe.model.can_write("Task")
				? this.menuButton(
						__(
							"Actions for {0} on {1}",
							[employee.employee_name, task.subject || task.name],
							"PM View"
						),
						(button) => this.openAssigneeMenu(button, { project, task, employee, chips, minutes })
				  )
				: null;
		return h(
			"div",
			{
				class: ["pm-cell", "pm-cell--employee", kind === "taskemployee" && "pm-cell--task-employee"],
				style: kind === "taskemployee" ? { "--pm-depth": depth } : null,
			},
			avatar(employee, "sm"),
			h(
				"div",
				{ class: "pm-cell-main" },
				h("span", { class: "pm-cell-title ellipsis", text: employee.employee_name }),
				activityType ? h("span", { class: "pm-cell-sub ellipsis", text: __(activityType) }) : null
			),
			over.length
				? overPill(over) // @dokos: es-badge through the status map
				: null,
			menu
		);
	}

	renderTaskCell({ project, task, assignees, depth = 0, children = [], expanded = false, mode }) {
		// @dokos: the assign button stays next to the assignees (several people per task)
		const assign = [
			assignees.length ? avatarStack(assignees, 2) : null,
			this.assignButton(
				__("Assign someone to {0}", [task.subject || task.name], "PM View"),
				() =>
					this.openAssignment({
						project: project.name,
						task: task.name,
						...this.taskAssignmentDates(project, task),
					}),
				{ quiet: assignees.length > 0 }
			),
		];
		// @dokos: an undated task with people on it: its work shows on the row, the button
		// dates the task from it (first and last assigned day)
		const undated = !task.exp_start_date && !task.exp_end_date;
		const datesButton =
			undated && assignees.length && frappe.model.can_write("Task")
				? h("button", {
						type: "button",
						class: "pm-nodate-btn",
						text: __("No dates", null, "PM View"),
						title: __("Set the expected dates from the assignments", null, "PM View"),
						attrs: {
							"aria-label": __(
								"{0} has no expected dates. Set them from the assignments",
								[task.subject || task.name],
								"PM View"
							),
						},
						on: { click: () => this.setTaskDatesFromAssignments(task) },
				  })
				: null;
		// @dokos: subtasks under their parent; a task opens on its subtasks or its team
		const name = task.subject || task.name;
		const rowId = resourceId.task(task.name);
		const expandable = children.length > 0 || assignees.length > 0;
		const toggle = expandable
			? this.toggleButton({ expanded, name, key: rowId, rowId })
			: h("span", { class: "pm-toggle-spacer", attrs: { "aria-hidden": "true" } });
		toggle.setAttribute("data-pm-key", `toggle::${rowId}`);
		let detail = null;
		if (expanded && children.length && assignees.length) {
			detail = this.modeSwitch({
				key: rowId,
				rowId,
				mode,
				name,
				choices: [
					["subtasks", __("Subtasks", null, "PM View"), children.length],
					["team", __("Team", null, "PM View"), assignees.length],
				],
			});
		} else if (!expanded && children.length) {
			detail = h("span", {
				class: "pm-subtask-count",
				text:
					children.length === 1
						? __("1 subtask", null, "PM View")
						: __("{0} subtasks", [children.length], "PM View"),
			});
		}
		return h(
			"div",
			{ class: "pm-cell pm-cell--task", style: { "--pm-depth": depth } },
			toggle,
			taskStatusDot(task.status),
			h(
				"div",
				{ class: "pm-cell-main" },
				this.dialogLink({
					class: "pm-cell-link ellipsis",
					doctype: "Task",
					name: task.name,
					text: task.subject || task.name,
					open: () => this.openTask(task.name),
				}),
				// @dokos: the status in words, the dot alone relies on color and shape
				h(
					"div",
					{ class: "pm-cell-line pm-cell-line--sub" },
					// the status in words gives way to the switch (visible as the dot, read aloud)
					h("span", {
						class: [
							"pm-cell-sub ellipsis",
							detail?.classList.contains("es-tab-buttons") && "sr-only",
						],
						text: taskStatusLabel(task.status),
					}),
					// the switch needs the room; the dates come back once it is collapsed
					detail?.classList.contains("es-tab-buttons") ? null : datesButton,
					detail
				)
			),
			assign
		);
	}

	renderAddCell({ project }) {
		const input = h("input", {
			type: "text",
			class: "pm-add-input",
			placeholder: __("Add a task — e.g. Write specs @Alice #Design 12h 22/09→25/09", null, "PM View"),
			title: __("Enter creates the Task and its Task Assignment", null, "PM View"),
			attrs: { "aria-label": __("New task in {0}", [project.project_name], "PM View") },
		});
		input.addEventListener("keydown", (e) => {
			if (e.key === "Enter" && !e.isComposing) {
				e.preventDefault();
				this.createQuickTask(project.name, input);
			} else if (e.key === "Escape") {
				input.value = "";
			}
		});
		return h("label", { class: "pm-cell pm-cell--add" }, icon("plus", "sm"), input);
	}

	renderPlannedCell(resource) {
		const props = resource.extendedProps;
		// @dokos: the full value as a tooltip, the column cuts a long one with an ellipsis
		const text = (value, cls = "") =>
			h("span", { class: `pm-planned ${cls}`, text: value, title: value || null });
		switch (props.kind) {
			case "project": {
				const { allocated_hours: allocated, planned_hours: planned } = props.project;
				// @dokos: "—" instead of "0h of 0h", so rows with work stand out. The words are
				// the tooltip and the accessible name only: "Nothing planned" clipped in the
				// 90px column ("ien de planifié").
				if (!allocated && !planned) {
					const nothing = __("Nothing planned", null, "PM View");
					return h(
						"div",
						{ class: "pm-planned-stack", title: nothing },
						h("span", {
							class: "pm-planned pm-planned-main pm-planned-empty",
							text: "—",
							attrs: { "aria-hidden": "true" },
						}),
						h("span", { class: "sr-only", text: nothing })
					);
				}
				const ratio = planned ? Math.min(1, allocated / planned) : 1;
				return h(
					"div",
					{ class: "pm-planned-stack" },
					text(formatHours(allocated), "pm-planned-main"),
					text(__("of {0}", [formatHours(planned)], "PM View"), "pm-planned-sub"),
					h(
						"span",
						{ class: "pm-bar pm-planned-bar", attrs: { "aria-hidden": "true" } },
						h("span", { class: "pm-bar-fill", style: { width: `${Math.round(ratio * 100)}%` } })
					)
				);
			}
			case "employee":
			case "taskemployee":
				return text(formatMinutes(props.minutes));
			case "team":
				return this.renderPlannedLoad(props.minutes, props.capacity);
			case "task": {
				// @dokos: assigned in the period "of" the expected time, as on the project rows
				const chips = this.payload?._index?.chipsByTask?.[props.task.name] || [];
				const assigned = chips.reduce((sum, ev) => sum + (ev.minutes || 0), 0);
				const expected = props.task.expected_time;
				if (!assigned) return text(expected ? formatHours(expected) : "");
				return h(
					"div",
					{ class: "pm-planned-stack" },
					text(formatMinutes(assigned), "pm-planned-main"),
					expected ? text(__("of {0}", [formatHours(expected)], "PM View"), "pm-planned-sub") : null
				);
			}
			case "load": {
				const totals = Object.values(this.payload ? this.teamLoad(this.payload) : {});
				const load = totals.reduce((sum, t) => sum + t.load, 0);
				const capacity = totals.reduce((sum, t) => sum + t.capacity, 0);
				return this.renderPlannedLoad(load, capacity);
			}
			default:
				return text("");
		}
	}

	/** @dokos: "Planned" cell of the Team load rows: the assigned total, what is left of the
	 * capacity over the period and a bar colored by load level. */
	renderPlannedLoad(load, capacity) {
		const { ratio } = loadLevel(load, capacity);
		const { level, text: remaining } = remainingText(load, capacity);
		const main = formatMinutes(load);
		return h(
			"div",
			{ class: ["pm-planned-stack", "pm-planned-load", `pm-level--${level}`] },
			h("span", { class: "pm-planned pm-planned-main", text: main, title: main }),
			h("span", {
				class: "pm-planned pm-planned-sub pm-planned-remaining",
				text: remaining,
				title: remaining,
			}),
			h(
				"span",
				{ class: "pm-bar pm-planned-bar", attrs: { "aria-hidden": "true" } },
				h("span", {
					class: "pm-bar-fill",
					style: { width: `${Math.min(100, Math.round(ratio * 100))}%` },
				})
			)
		);
	}

	toggleProject(project, expanded, rowId = resourceId.project(project)) {
		expandedProjects.set(project, expanded);
		this.fullcalendar?.refetchResources();
		if (project === TEAM_EXPANDED_KEY) this.fullcalendar?.refetchEvents(); // @dokos: team load events
		// the row is re-rendered: give the focus back to its (new) toggle
		setTimeout(() => {
			const id = CSS.escape(rowId);
			this.calendar_element?.querySelector(`td[data-resource-id="${id}"] .pm-toggle`)?.focus();
		}, 50);
	}

	// -------------------------------------------------------------------------------
	// Events rendering
	// -------------------------------------------------------------------------------

	/** @override */ eventContent({ event }) {
		const props = event.extendedProps;
		switch (props.kind) {
			case "chip":
				return {
					domNodes: [
						renderChip({
							minutes: props.data.minutes,
							activityType: props.data.activity_type,
							over: props.over,
						}),
					],
				};
			case "sum":
				return { domNodes: [this.renderRatio(props.minutes, props.capacity, "pm-sum-body")] };
			case "load":
				return {
					domNodes: [
						props.capacity || props.load
							? this.renderLoad(props.load, props.capacity)
							: h("span"),
					],
				};
			case "coverage":
				return { domNodes: [this.renderCoverage(props)] };
			case "task": {
				const names = props.assignees
					.map((a) => (a.employee_name || a.employee || "").split(" ")[0])
					.join(", ");
				const label = [
					names || __("Unassigned", null, "PM View"),
					props.activityType && __(props.activityType),
				]
					.filter(Boolean)
					.join(" · ");
				return {
					domNodes: [
						h(
							"div",
							{ class: "pm-task-bar-body ellipsis" },
							props.color ? swatch(props.color) : null,
							h("span", { class: "ellipsis", text: label })
						),
					],
				};
			}
			default:
				return super.eventContent({ event });
		}
	}

	/**
	 * @dokos: a coverage segment: one cell per day (the segment spans whole day columns) with
	 * its hours, the first one with the people's first names below.
	 */
	renderCoverage({ segment, people }) {
		const names = this.firstNames(people);
		return h(
			"div",
			{ class: "pm-cov-body", attrs: { "aria-hidden": "true" } },
			segment.days.map((day, index) =>
				h(
					"span",
					{ class: ["pm-cov-day", day.over && "is-over"] },
					h(
						"span",
						{ class: "pm-cov-hours" },
						day.over ? overMark(false) : null,
						formatMinutesShort(day.minutes)
					),
					index === 0 ? h("span", { class: "pm-cov-names ellipsis", text: names }) : null
				)
			)
		);
	}

	firstNames(people) {
		return people.map((p) => (p.employee_name || p.employee || "").split(" ")[0]).join(", ");
	}

	/** @dokos: the spoken / tooltip summary of a coverage segment. */
	coverageLabel({ task, segment, people }) {
		const over = segment.days.filter((d) => d.over).map((d) => formatDay(d.date, "weekdayDayMonth"));
		const total = segment.days.reduce((sum, d) => sum + d.minutes, 0);
		return [
			task.subject || task.name,
			people.map((p) => p.employee_name || p.employee).join(", "),
			segment.start === segment.end
				? formatDay(segment.start, "weekdayDayMonth")
				: formatDayRange(segment.start, segment.end),
			formatMinutes(total),
			over.length ? __("over capacity on {0}", [over.join(", ")], "PM View") : null,
		]
			.filter(Boolean)
			.join(" · ");
	}

	/** "14h" + a bar, or "32h / 40h" + a bar colored by load level. */
	renderRatio(load, capacity, cls, withCapacity = false) {
		const { ratio, level } = loadLevel(load, capacity);
		const label = withCapacity
			? `${formatMinutes(load)} / ${formatMinutes(capacity)}`
			: formatMinutes(load);
		return h(
			"div",
			{ class: [cls, `pm-level--${level}`] },
			h("span", { class: "pm-ratio-label" }, level === "over" ? overMark() : null, label),
			h(
				"span",
				{ class: "pm-bar", attrs: { "aria-hidden": "true" } },
				h("span", {
					class: "pm-bar-fill",
					style: { width: `${Math.min(100, Math.round(ratio * 100))}%` },
				})
			)
		);
	}

	/**
	 * @dokos: a Team load day cell, narrow enough for the 2-week and month columns: what is
	 * left of the capacity ("3h", "▲ -2h59") above the assigned load (hidden in the month view),
	 * then a bar colored by load level. The exact values are in the tooltip.
	 */
	renderLoad(load, capacity) {
		const { ratio, level } = loadLevel(load, capacity);
		const remaining = capacity - load;
		return h(
			"div",
			{ class: ["pm-load-body", `pm-level--${level}`] },
			h(
				"span",
				{ class: "pm-load-remaining" },
				level === "over" ? overMark(false) : null,
				formatMinutesShort(remaining)
			),
			h("span", { class: "pm-load-assigned", text: formatMinutesShort(load) }),
			h(
				"span",
				{ class: "pm-bar", attrs: { "aria-hidden": "true" } },
				h("span", {
					class: "pm-bar-fill",
					style: { width: `${Math.min(100, Math.round(ratio * 100))}%` },
				})
			)
		);
	}

	/** @override */ onEventDidMount({ el, event }) {
		const props = event.extendedProps;
		if (props.color) el.style.setProperty("--pm-color", props.color);
		if (props.kind === "chip") {
			const hint = event.startEditable
				? __("Drag to move the whole assignment (every day of it).", null, "PM View")
				: "";
			el.title = [props.tooltip, hint].filter(Boolean).join("\n");
			el.setAttribute("aria-label", props.tooltip);
			el.setAttribute("aria-haspopup", "dialog"); // @dokos: the chip actions
		} else if (props.kind === "coverage") {
			this.mountCoverage(el, props);
		} else if (props.kind === "task") {
			const summary = [
				props.task.subject,
				props.assignees.map((a) => a.employee_name || a.employee).join(", ") ||
					__("Unassigned", null, "PM View"),
				props.task.expected_time ? formatHours(props.task.expected_time) : null,
			];
			el.title = [
				...summary,
				__("Task expected dates", null, "PM View"),
				event.startEditable ? __("drag or stretch to change them", null, "PM View") : null,
			]
				.filter(Boolean)
				.join(" · ");
			// @dokos: FullCalendar makes the bar focusable (eventInteractive); name it with its dates
			const last = moment(event.end).subtract(1, "day");
			el.setAttribute(
				"aria-label",
				[...summary, formatDayRange(ymd(event.start), ymd(last))].filter(Boolean).join(", ")
			);
			el.setAttribute("aria-haspopup", "dialog");
		} else if (props.kind === "load" && (props.load || props.capacity)) {
			// @dokos: the exact values behind the rounded ones of the cell
			const label = [
				formatDay(ymd(event.start), "weekdayDayMonth"),
				__("Assigned {0}", [formatMinutes(props.load)], "PM View"),
				__("Available {0}", [formatMinutes(props.capacity)], "PM View"),
				remainingText(props.load, props.capacity).text,
			].join(" · ");
			el.title = label;
			el.setAttribute("aria-label", label);
		}
	}

	/**
	 * @dokos: a coverage segment is a background event (it must not stack with the task bars),
	 * which FullCalendar leaves inert: it becomes a button here. Its pointer events stay
	 * on it, so a click opens its actions instead of starting a day selection.
	 */
	mountCoverage(el, props) {
		const label = this.coverageLabel(props);
		el.title = label;
		el.tabIndex = 0;
		el.setAttribute("role", "button");
		el.setAttribute("aria-label", label);
		el.setAttribute("aria-haspopup", "dialog");
		const stop = (e) => e.stopPropagation();
		el.addEventListener("mousedown", stop);
		el.addEventListener("touchstart", stop, { passive: true });
		el.addEventListener("click", (e) => {
			e.stopPropagation();
			this.openCoveragePopover(el, props);
		});
		el.addEventListener("keydown", (e) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				this.openCoveragePopover(el, props);
			}
		});
	}

	/** @override */ onEventWillUnmount() {}

	// -------------------------------------------------------------------------------
	// Legend
	// -------------------------------------------------------------------------------

	renderLegend() {
		const container = this.legendContainer;
		if (!container) return;
		container.replaceChildren();
		const payload = this.payload;
		if (!payload) return;

		const used = new Map();
		for (const ev of payload.events) {
			if (ev.activity_type && !used.has(ev.activity_type)) {
				used.set(ev.activity_type, activityColor(ev.activity_type, ev.activity_color));
			}
		}
		// @dokos: dk-legend row items (the container is a .dk-legend)
		const item = (mark, label) =>
			h("span", { class: "pm-legend-item" }, mark, h("span", { class: "legend-label", text: label }));
		for (const [name, color] of used) container.append(item(swatch(color), __(name)));
		// @dokos: every mark of the timeline, each one distinct without color
		const box = (cls) => h("span", { class: `pm-swatch ${cls}`, attrs: { "aria-hidden": "true" } });
		container.append(
			item(box("pm-swatch--coverage"), __("Assigned work", null, "PM View")),
			item(box("pm-swatch--unassigned"), __("Unassigned", null, "PM View")),
			item(
				h(
					"span",
					{ class: "pm-swatch pm-swatch--over", attrs: { "aria-hidden": "true" } },
					overMark(false)
				),
				__("Over capacity", null, "PM View")
			),
			item(box("pm-swatch--weekend"), __("Weekend", null, "PM View"))
		);
	}

	// -------------------------------------------------------------------------------
	// Interactions
	// -------------------------------------------------------------------------------

	openAssignment(values, name = null) {
		return openTaskAssignmentDialog({
			name,
			values,
			on_saved: () => this.refresh(),
		});
	}

	openProjectQuickEntry() {
		return openProjectQuickEntry({ on_saved: () => this.refresh() });
	}

	/** @dokos: quick edit dialog of a project of the plan. */
	openProject(project) {
		return openProjectDialog(project.name, {
			summary: { allocated_hours: project.allocated_hours, planned_hours: project.planned_hours },
			on_saved: () => this.refresh(),
		});
	}

	/** @dokos: quick edit dialog of a task. */
	openTask(name) {
		return openTaskDialog(name, { on_saved: () => this.refresh() });
	}

	/**
	 * @dokos: link to the form that opens the quick dialog on a plain click; Ctrl/Cmd,
	 * Shift and middle clicks keep the link (new tab or window).
	 */
	dialogLink({ doctype, name, text, open, class: cls }) {
		return h("a", {
			class: cls,
			href: frappe.utils.get_form_link(doctype, name),
			text,
			title: text,
			attrs: { "aria-haspopup": "dialog" },
			on: {
				click: (e) => {
					if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
					e.preventDefault();
					e.stopPropagation(); // the desk router would follow the link
					open();
				},
			},
		});
	}

	/** @override */ onSelect(info) {
		const props = info.resource?.extendedProps || {};
		const values = {
			project: props.project?.name,
			start_date: info.startStr.slice(0, 10),
			end_date: addDays(info.endStr.slice(0, 10), -1),
		};
		if (props.kind === "employee") {
			values.employees = [props.employee.employee];
			values.activity_type = props.activityType || undefined;
		} else if (props.kind === "team") {
			values.employees = [props.employee.employee]; // @dokos: the project is chosen in the dialog
		} else if (props.kind === "task") {
			values.task = props.task.name;
			values.employees = props.task.assigned_employees.slice(0, 1);
		} else if (props.kind === "taskemployee") {
			values.task = props.task.name; // @dokos: an employee of a task Team row
			values.employees = [props.employee.employee];
			values.activity_type = props.activityType || undefined;
		}
		this.fullcalendar.unselect();
		this.openAssignment(values);
	}

	/** @override */ onEventClick(info) {
		const props = info.event.extendedProps;
		info.jsEvent?.preventDefault();
		if (props.kind === "chip") {
			this.openChipPopover(info.el, props); // @dokos: its actions, the full dialog is one of them
		} else if (props.kind === "task") {
			this.openTask(props.task.name); // @dokos: quick dialog, "Edit Full Form" in it
		}
	}

	// -------------------------------------------------------------------------------
	// @dokos: contextual actions (work chip, coverage segment, assignee row)
	// -------------------------------------------------------------------------------

	/** The actions of one day of work: edit, remove the day, give it to someone else, delete. */
	openChipPopover(anchor, { data: ev, over }) {
		const { employees, taskByName } = this.payload._index;
		const employee = employees[ev.employee] || { employee: ev.employee };
		const name = employee.employee_name || ev.employee;
		const task = ev.task ? taskByName[ev.task] : null;
		const shared = (ev.assignees || []).length > 1;
		const multiDay = ev.assignment_start !== ev.assignment_end;
		const canWrite = frappe.model.can_write("Task Assignment");
		const day = formatDay(ev.date, "weekdayDayMonth");
		const range = multiDay ? formatDayRange(ev.assignment_start, ev.assignment_end) : day;
		const load = this.payload.load?.[ev.employee]?.[ev.date] || 0;
		const capacity = this.payload.capacity?.[ev.employee]?.[ev.date] || 0;
		openPopover(anchor, {
			label: __("Work of {0} on {1}", [name, day], "PM View"),
			content: [
				h(
					"div",
					{ class: "pm-popover-head" },
					avatar(employee, "sm"),
					h("span", { class: "pm-popover-title ellipsis", text: name }),
					over ? overPill([ev.date]) : null
				),
				popoverFact(__("Task"), task?.subject || __("No task", null, "PM View")),
				popoverFact(
					__("Assignment", null, "PM View"),
					__("{0} · {1} a day", [range, formatMinutes(ev.minutes)], "PM View")
				),
				popoverFact(
					__("Load on {0}", [day], "PM View"),
					`${formatMinutes(load)} / ${formatMinutes(capacity)}`
				),
				h(
					"div",
					{ class: "pm-popover-items" },
					popoverItem({
						label: __("Edit…", null, "PM View"),
						action: () => this.openAssignment({}, ev.task_assignment),
					}),
					// a one-day assignment of one person: removing the day is deleting it
					canWrite && (multiDay || shared)
						? popoverItem({
								label: __("Remove this day", null, "PM View"),
								hint: day,
								action: () => this.removeDay(ev),
						  })
						: null,
					canWrite
						? popoverItem({
								label: __("Replace by…", null, "PM View"),
								action: () =>
									this.pickEmployee(anchor, {
										label: __("Replace {0} on this assignment", [name], "PM View"),
										exclude: [ev.employee],
										onPick: (to) =>
											this.runAction(() =>
												this.reassignAssignment(
													ev.task_assignment,
													ev.employee,
													to,
													0
												)
											),
									}),
						  })
						: null,
					frappe.model.can_delete("Task Assignment")
						? popoverItem({
								label: __("Delete the assignment", null, "PM View"),
								hint: shared
									? __("{0} people · {1}", [ev.assignees.length, range], "PM View")
									: range,
								danger: true,
								action: () => this.deleteAssignment(ev.task_assignment),
						  })
						: null
				),
			],
		});
	}

	/** The work of a task row over a run of days: one line per assignment and person. */
	openCoveragePopover(anchor, { project, task, segment }) {
		const { employees } = this.payload._index;
		const groups = new Map();
		for (const day of segment.days) {
			for (const ev of day.chips) {
				const key = `${ev.task_assignment}::${ev.employee}`;
				const group = groups.get(key) || { ev, minutes: 0, days: 0 };
				group.minutes += ev.minutes || 0;
				group.days += 1;
				groups.set(key, group);
			}
		}
		const subject = task.subject || task.name;
		const range =
			segment.start === segment.end
				? formatDay(segment.start, "weekdayDayMonth")
				: formatDayRange(segment.start, segment.end);
		openPopover(anchor, {
			label: __("Work on {0}, {1}", [subject, range], "PM View"),
			content: [
				h(
					"div",
					{ class: "pm-popover-head" },
					h("span", { class: "pm-popover-title ellipsis", text: subject })
				),
				popoverFact(__("Period", null, "PM View"), range),
				h(
					"div",
					{ class: "pm-popover-items" },
					[...groups.values()].map(({ ev, minutes, days }) =>
						popoverItem({
							label: __(
								"Edit the assignment of {0}",
								[(employees[ev.employee] || {}).employee_name || ev.employee],
								"PM View"
							),
							hint:
								days === 1
									? formatMinutes(minutes)
									: __("{0} over {1} days", [formatMinutes(minutes), days], "PM View"),
							action: () => this.openAssignment({}, ev.task_assignment),
						})
					),
					popoverItem({
						label: __("Show the team of the task", null, "PM View"),
						action: () => this.showTaskTeam(task),
					}),
					frappe.model.can_create("Task Assignment")
						? popoverItem({
								label: __("Assign someone…", null, "PM View"),
								action: () =>
									this.openAssignment({
										project: project.name,
										task: task.name,
										start_date: segment.start,
										end_date: segment.end,
									}),
						  })
						: null
				),
			],
		});
	}

	/** The actions of a person on a task (and its subtasks), whatever the number of assignments. */
	openAssigneeMenu(anchor, { project, task, employee, minutes }) {
		const name = employee.employee_name || employee.employee;
		const subject = task.subject || task.name;
		openPopover(anchor, {
			label: __("Actions for {0} on {1}", [name, subject], "PM View"),
			content: [
				h(
					"div",
					{ class: "pm-popover-head" },
					avatar(employee, "sm"),
					h("span", { class: "pm-popover-title ellipsis", text: name })
				),
				h(
					"div",
					{ class: "pm-popover-items" },
					frappe.model.can_create("Task Assignment")
						? popoverItem({
								label: __("Add days…", null, "PM View"),
								action: () =>
									this.openAssignment({
										project: project.name,
										task: task.name,
										employees: [employee.employee],
										...this.taskAssignmentDates(project, task),
									}),
						  })
						: null,
					popoverItem({
						label: __("Replace by…", null, "PM View"),
						action: () =>
							this.pickEmployee(anchor, {
								label: __("Replace {0} on {1}", [name, subject], "PM View"),
								exclude: [employee.employee],
								onPick: (to) =>
									this.runAction(
										() =>
											frappe.xcall(ACTIONS.replaceOnTask, {
												task: task.name,
												employee: employee.employee,
												new_employee: to,
											}),
										(result) => __("{0} assignments updated", [result.updated], "PM View")
									),
							}),
					}),
					popoverItem({
						label: __("Remove from the task", null, "PM View"),
						hint: minutes ? formatMinutes(minutes) : null,
						danger: true,
						action: () => this.removeFromTask(task, employee),
					})
				),
			],
		});
	}

	/** A popover with an Employee Link control; `onPick` gets the chosen employee. */
	pickEmployee(anchor, { label, exclude = [], onPick }) {
		const wrapper = h("div", { class: "pm-popover-picker" });
		const control = frappe.ui.form.make_control({
			parent: wrapper,
			df: {
				fieldtype: "Link",
				fieldname: "pm_replace_employee",
				options: "Employee",
				placeholder: __("Employee"),
				get_query: () => ({
					filters: { status: "Active", ...(exclude.length ? { name: ["not in", exclude] } : {}) },
				}),
				change: () => {
					const employee = control.get_value();
					if (!employee) return;
					closePopover({ restoreFocus: false });
					onPick(employee);
				},
			},
			render_input: true,
			only_input: true,
		});
		openPopover(anchor, {
			label,
			content: [h("div", { class: "pm-popover-title", text: label }), wrapper],
		});
		control.$input?.trigger("focus");
	}

	showTaskTeam(task) {
		const key = resourceId.task(task.name);
		rowModes.set(key, "team");
		this.toggleProject(key, true, key);
	}

	/**
	 * Run a server action, then redraw the plan (also after a failure: the server message is
	 * already shown). `success`: alert text, or a function of the result giving it.
	 */
	async runAction(action, success = null) {
		try {
			const result = await action();
			if (success) {
				const message = typeof success === "function" ? success(result) : success;
				frappe.show_alert({ message, indicator: "green" });
			}
			return result;
		} catch (e) {
			return undefined;
		} finally {
			this.refresh();
		}
	}

	removeDay(ev) {
		return this.runAction(
			() =>
				frappe.xcall(ACTIONS.removeDays, {
					task_assignment: ev.task_assignment,
					employee: ev.employee,
					start_date: ev.date,
				}),
			__("{0} removed from the assignment", [formatDay(ev.date, "weekdayDayMonth")], "PM View")
		);
	}

	/** Delete at once, with an Undo in the alert (no confirmation to click through first). */
	async deleteAssignment(name) {
		const values = await this.runAction(() => frappe.xcall(ACTIONS.delete, { task_assignment: name }));
		if (!values) return;
		this.undoToast(__("Assignment deleted", null, "PM View"), () =>
			this.runAction(
				() => frappe.xcall(ACTIONS.restore, { values: JSON.stringify(values) }),
				__("Assignment restored", null, "PM View")
			)
		);
	}

	undoToast(text, undo) {
		let toast = null;
		const button = h("button", {
			type: "button",
			class: "btn btn-xs btn-default pm-undo-btn",
			text: __("Undo"),
			on: {
				click: () => {
					button.disabled = true;
					toast?.remove?.();
					undo();
				},
			},
		});
		toast = frappe.show_alert(
			{ message: h("span", { class: "pm-undo" }, h("span", { text }), button), indicator: "green" },
			UNDO_SECONDS
		);
	}

	removeFromTask(task, employee) {
		const name = frappe.utils.escape_html(employee.employee_name || employee.employee);
		const subject = frappe.utils.escape_html(task.subject || task.name);
		frappe.confirm(
			__(
				"Remove {0} from {1} and its subtasks? Their assignments on these tasks are deleted, also outside the displayed period.",
				[`<b>${name}</b>`, `<b>${subject}</b>`],
				"PM View"
			),
			() =>
				this.runAction(
					() =>
						frappe.xcall(ACTIONS.removeFromTask, {
							task: task.name,
							employee: employee.employee,
						}),
					(result) =>
						__(
							"{0} removed: {1} assignments deleted, {2} updated",
							[name, result.deleted, result.updated],
							"PM View"
						)
				)
		);
	}

	setTaskDatesFromAssignments(task) {
		return this.runAction(
			() => frappe.xcall(ACTIONS.datesFromAssignments, { task: task.name }),
			(result) =>
				__(
					"{0} is planned {1}",
					[
						frappe.utils.escape_html(task.subject || task.name),
						formatDayRange(datePart(result.exp_start_date), datePart(result.exp_end_date)),
					],
					"PM View"
				)
		);
	}

	/** @dokos: a chip can only land on an employee row of its own project. */
	eventAllow(info, event) {
		const props = event.extendedProps;
		if (props.kind !== "chip") return true;
		const target = info.resource?.extendedProps;
		// @dokos: a chip of a task Team row stays on that task's Team rows
		if (props.rowTask) return target?.kind === "taskemployee" && target.task?.name === props.rowTask;
		return target?.kind === "employee" && target.project?.name === props.data.project;
	}

	/** @override */ async onEventDrop(info) {
		const props = info.event.extendedProps;
		const delta = dayDelta(info.oldEvent.start, info.event.start);
		const newEmployee = info.newResource?.extendedProps?.employee?.employee; // @dokos
		try {
			if (props.kind === "chip" && newEmployee && newEmployee !== props.data.employee) {
				await this.reassignAssignment(
					props.data.task_assignment,
					props.data.employee,
					newEmployee,
					delta
				);
			} else if (props.kind === "chip") {
				await this.moveAssignment(props.data.task_assignment, delta);
			} else if (props.kind === "task") {
				await this.updateTaskDates(props.task, delta, delta);
			} else {
				info.revert();
				return;
			}
		} catch (e) {
			info.revert();
			return;
		}
		this.refresh();
	}

	/** @override */ async onEventResize(info) {
		const props = info.event.extendedProps;
		if (props.kind !== "task") {
			info.revert();
			return;
		}
		const startDelta = dayDelta(info.oldEvent.start, info.event.start);
		const endDelta = dayDelta(info.oldEvent.end, info.event.end);
		try {
			await this.updateTaskDates(props.task, startDelta, endDelta);
		} catch (e) {
			info.revert();
			return;
		}
		this.refresh();
	}

	/** Move every day of a Task Assignment by `delta` days (chips are daily slices). */
	async moveAssignment(name, delta) {
		if (!delta) return;
		const res = await frappe.db.get_value("Task Assignment", name, ["start_date", "end_date"]);
		const { start_date, end_date } = res?.message || {};
		if (!start_date || !end_date) throw new Error("Task Assignment not found");
		await this.setValues("Task Assignment", name, {
			start_date: addDays(start_date, delta),
			end_date: addDays(end_date, delta),
		});
	}

	/** @dokos: replace `from` by `to` in the assignees (whole assignment), shifted by `delta` days. */
	async reassignAssignment(name, from, to, delta) {
		try {
			const doc = await frappe.xcall("frappe.client.get", { doctype: "Task Assignment", name });
			const rows = doc.assigned_to || [];
			if (rows.some((r) => r.employee === to)) {
				doc.assigned_to = rows.filter((r) => r.employee !== from);
			} else {
				for (const row of rows) if (row.employee === from) row.employee = to;
			}
			if (delta) {
				doc.start_date = addDays(doc.start_date, delta);
				doc.end_date = addDays(doc.end_date, delta);
			}
			await frappe.xcall("frappe.client.save", { doc });
		} catch (e) {
			frappe.show_alert({
				message: __("Could not update {0} {1}", [__("Task Assignment"), name], "PM View"),
				indicator: "red",
			});
			throw e;
		}
	}

	/** Shift the Task expected dates (Datetime: the time of day is kept). */
	async updateTaskDates(task, startDelta, endDelta) {
		const shift = (value, delta) =>
			moment(value, frappe.defaultDatetimeFormat)
				.add(delta, "days")
				.format(frappe.defaultDatetimeFormat);
		const start = task.exp_start_date || task.exp_end_date;
		const end = task.exp_end_date || task.exp_start_date;
		await this.setValues("Task", task.name, {
			exp_start_date: shift(start, startDelta),
			exp_end_date: shift(end, endDelta),
		});
	}

	async setValues(doctype, name, values) {
		try {
			await frappe.xcall("frappe.client.set_value", { doctype, name, fieldname: values });
		} catch (e) {
			frappe.show_alert({
				message: __("Could not update {0} {1}", [__(doctype), name], "PM View"),
				indicator: "red",
			});
			throw e;
		}
	}

	async createQuickTask(project, input) {
		const text = input.value.trim();
		if (!text || input.disabled) return;
		input.disabled = true;
		try {
			const result = await frappe.xcall(QUICK_TASK_METHOD, { project, text });
			frappe.show_alert({
				message: result.task_assignment
					? __("Task {0} and its assignment created", [result.task], "PM View")
					: __("Task {0} created", [result.task], "PM View"),
				indicator: "green",
			});
			for (const warning of result.warnings || []) {
				frappe.show_alert({ message: safeBoldHtml(warning), indicator: "orange" }, 8);
			}
			expandedProjects.set(project, true);
			this.refresh();
		} finally {
			input.disabled = false;
		}
	}
}
